import { CancelIcon } from "@gadgetinc/themes/src/icons/CancelIcon";
import { DocsInfoIcon } from "@gadgetinc/themes/src/icons/DocsInfoIcon";
import { FailureCircleIcon } from "@gadgetinc/themes/src/icons/FailureCircleIcon";
import { SuccessCircleIcon } from "@gadgetinc/themes/src/icons/SuccessCircleIcon";
import { WarningCircleIcon } from "@gadgetinc/themes/src/icons/WarningCircleIcon";
import { expandBaseWebBorder, expandBorderRadii, paddingUtil } from "@gadgetinc/themes/src/utils";
import { useStyletron } from "baseui";
import type { Theme } from "baseui/theme";
import { merge } from "lodash";
import type { ReactNode } from "react";
import React from "react";
import type { StyleObject } from "styletron-react";
import { Column } from "../../Column";
import { Row } from "../../Row";
import { KIND as BUTTON_KIND, Button } from "../editor/Button";

export enum KIND {
  neutral,
  info,
  positive,
  warning,
  negative,
}

const getStylesForKind = <T extends Theme>($theme: T, kind?: KIND) => {
  switch (kind) {
    case KIND.positive:
      return {
        ...expandBaseWebBorder({
          ...$theme.borders.border100,
          borderColor: $theme.colors.borderPositive,
        }),
        backgroundColor: $theme.colors.backgroundPositive,
      };
    case KIND.warning:
      return {
        ...expandBaseWebBorder({
          ...$theme.borders.border100,
          borderColor: $theme.colors.borderWarning,
        }),
        backgroundColor: $theme.colors.backgroundWarning,
      };
    case KIND.negative:
      return {
        ...expandBaseWebBorder({
          ...$theme.borders.border100,
          borderColor: $theme.colors.borderNegative,
        }),
        backgroundColor: $theme.colors.backgroundNegative,
      };
    case KIND.info:
      return {
        ...expandBaseWebBorder({
          ...$theme.borders.border100,
          borderColor: $theme.colors.borderInfo,
        }),
        backgroundColor: $theme.colors.backgroundInfo,
      };
    default:
      return {
        ...expandBaseWebBorder({
          ...$theme.borders.border100,
          borderColor: $theme.colors.primary200,
        }),
        backgroundColor: $theme.colors.primary50,
      };
  }
};

export const getIconForKind = <T extends Theme>($theme: T, kind?: KIND) => {
  switch (kind) {
    case KIND.info:
      return <DocsInfoIcon color={$theme.colors.contentInfo} />;
    case KIND.positive:
      return <SuccessCircleIcon color={$theme.colors.contentPositive} />;
    case KIND.warning:
      return <WarningCircleIcon color={$theme.colors.contentWarning} />;
    case KIND.negative:
      return <FailureCircleIcon color={$theme.colors.contentNegative} />;
    default:
      return null;
  }
};

export const Note = (props: { children?: ReactNode; KIND?: KIND; $style?: StyleObject; noIcon?: boolean; onClose?: () => void }) => {
  const [_, $theme] = useStyletron();
  const icon = getIconForKind($theme, props.KIND);

  const style = merge(
    {
      ...expandBorderRadii($theme.borders.surfaceBorderRadius),
      ...getStylesForKind($theme, props.KIND),
      ...paddingUtil($theme.sizing.scale500, $theme.sizing.scale600),
      ...$theme.typography.LabelSmall,
      color: $theme.colors.contentPrimary,
      justifyContent: "center",
    },
    props.$style
  );

  return (
    <Column $style={style}>
      <Row $style={{ alignItems: "start" }}>
        {icon && !props.noIcon && (
          <Column $style={{ paddingTop: $theme.sizing.scale0, paddingRight: $theme.sizing.scale300 }}>{icon}</Column>
        )}
        <Column $style={{ display: "inline", flex: 1 }}>{props.children}</Column>
        {!!props.onClose && (
          <Button size="square" kind={BUTTON_KIND.tertiary} onClick={props.onClose}>
            <CancelIcon />
          </Button>
        )}
      </Row>
    </Column>
  );
};
